/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import { gql } from '@apollo/client';

export const CONDITIONS_FIELDS = gql`
	fragment ConditionsFields on Condition {
		questionId
		answerId
		union
		operator
	}
`;

export const MULTIMEDIA_FIELDS = gql`
	fragment MultimediaFields on Multimedia {
		type
		thumbnails {
			text
			images {
				url
				name
				title
			}
			size
			imageView
			onClick
		}
		tabs {
			list {
				label
				description
			}
		}
	}
`;

export const PROTOTYPE_QUESTIONS_FIELDS = gql`
	fragment PrototypeQuestionsFields on Question {
		__typename
		... on TimeoutQuestion {
			type
			questionId
			text
			mandatory
			conditions {
				questionId
				answerId
				union
				operator
			}
			hasLogic
			taskBarPosition
			showStartTask
			showCountdown
			exposure {
				autoNextAfterTimeout
				time
			}
			image {
				url
				align
			}
		}
		... on ClickTestQuestion {
			type
			questionId
			text
			mandatory
			conditions {
				questionId
				answerId
				union
				operator
			}
			hasLogic
			taskBarPosition
			showStartTask
			showSkip
			mandatoryClicks {
				min
				max
				autoNext
			}
			clicks {
				show
				showNumber
				size
				color
			}
			image {
				url
				align
			}
		}
		... on TreeTestQuestion {
			type
			questionId
			text
			mandatory
			conditions {
				questionId
				answerId
				union
				operator
			}
			hasLogic
			autoNext
			showStartTask
			showSkip
			treeType
			tree
			answerOptions {
				answerId
				label
				nodeId
			}
		}
	}
`;

export const CARD_SORT_TASK_FIELDS = gql`
	fragment CardSortTaskFields on CardSort {
		__typename
		taskId
		id
		description
		showInstructions
		askConfirmation
		columsNumber
		numCategories
		maxNumCategories
		askForName
		askForDescription
		cardSortType
		categories {
			categoryId
			label
			description
		}
		cards {
			cardId
			label
			description
			image
			thumbnail
		}
	}
`;

export const QUESTIONNAIRE_FIELDS = gql`
	${CONDITIONS_FIELDS}
	${MULTIMEDIA_FIELDS}
	${PROTOTYPE_QUESTIONS_FIELDS}

	fragment QuestionnaireFields on Questionnaire {
		__typename
		id
		progress {
			totalQuestions
			completedQuestions
		}
		page {
			number
			title
			questions {
				__typename
				... on ChoiceQuestion {
					questionId
					answerOptions {
						id
						label
						type
						hasLogic
						questionLogic
					}
					conditions {
						...ConditionsFields
					}
					text
					mandatory
					type
					comments
					columnsLayout
					mandatoryAnswer {
						min
						max
					}
					answer {
						answers {
							id
							value
						}
						comments
					}
					multimedia {
						...MultimediaFields
					}
				}
				... on RankOrderQuestion {
					type
					questionId
					text
					topLabel
					bottomLabel
					mandatory
					rankingPoints
					answerOptions {
						id
						label
					}
					conditions {
						...ConditionsFields
					}
					comments
					hasLogic
					multimedia {
						...MultimediaFields
					}
				}
				... on OpenEndedQuestion {
					type
					questionId
					text
					mandatory
					answerOptions {
						id
						label
					}
					conditions {
						...ConditionsFields
					}
					comments
					hasLogic
					answer {
						answers {
							id
							value
						}
						comments
					}
					multimedia {
						...MultimediaFields
					}
				}
				... on TextOnlyQuestion {
					type
					questionId
					text
					conditions {
						...ConditionsFields
					}
					hasLogic
				}
				... on MatrixQuestion {
					type
					choices {
						__typename
						... on MatrixChoicesCustom {
							type
						}
						... on MatrixChoicesNumeric {
							type
							numeric {
								minLabel
								midLabel
								maxLabel
							}
						}
						... on MatrixChoicesStars {
							type
							stars {
								color
							}
						}
					}
					questionId
					text
					mandatory
					conditions {
						...ConditionsFields
					}
					rows {
						id
						label
					}
					columns {
						id
						label
						type
						hasLogic
						questionLogic
					}
					comments
					answer {
						rows {
							id
							columns {
								id
								value
							}
						}
						comments
					}
					multimedia {
						...MultimediaFields
					}
				}
				...PrototypeQuestionsFields
			}
		}
	}
`;

export const TASKS_FIELDS = gql`
	${QUESTIONNAIRE_FIELDS}
	${PROTOTYPE_QUESTIONS_FIELDS}
	${CARD_SORT_TASK_FIELDS}

	fragment TasksFields on Task {
		__typename
		... on Questionnaire {
			...QuestionnaireFields
		}
		... on Prototype {
			id
			question {
				__typename
				...PrototypeQuestionsFields
			}
		}
		... on CardSort {
			__typename
			...CardSortTaskFields
		}
		... on Finished {
			url
		}
	}
`;
